import {
  CHANGE_HUB_FILTER,
  HIDE_CONNECTIONS_HUB_POPUP,
  HIDE_PARTNERSHIP_CODE_SUCCESS_POPUP,
  HIDE_REGISTRATION_SUCCESS_POPUP,
  NPS_REVIEW_SUBMITTED,
  SHOW_CONNECTIONS_HUB_POPUP,
  SHOW_REGISTRATION_SUCCESS_POPUP,
} from '~/actions';
import { BestInDental } from '~/actions/BestInDental';
import { WC_MANAGEMENT_STATUS } from '~/components/WorkerClassificationManagement/constants';
import { shouldUseDsoStructure } from '~/growthbook';
import { IPartnerReferral, IRecommendedProfessional, IUser } from '~/models';
import { apiMethodsConst } from '~/services/methods';
import type {
  IUserReducerState,
  TDismissWinnerPopupAction,
  TSelectedChildOffice,
  TUserAction,
} from './interfaces';

export const initialState: IUserReducerState = {
  selectedChildOffice: null,
  selectedOfficeId: null,
  user: null,
  offices: [],
  childOffices: [],
  filteredChilds: null,
  pendingPhoneVerification: false,
  userEducation: null,
  officeEducation: null,
  allProfessions: [
    {
      code: 'RDH',
      name: 'Dental Hygienist',
    },
    {
      code: 'DA',
      name: 'Dental Assistant',
    },
    {
      code: 'DN',
      name: 'Associate Dentist',
    },
    {
      code: 'FD',
      name: 'Front Desk',
    },
  ],
  allSpecializations: [],
  allSoftwares: [],
  allMetadata: null,
  notificationSettings: null,
  isProfessionalHubInitialized: false,
  favoriteProfessionals: [],
  recentProfessionals: [],
  recommendedProfessionals: [],
  professionalHubFilter: 'all',
  partnershipOrganizations: [],
  showRegistrationSuccessPopup: false,
  showPartnershipCodeSuccess: false,
  showSuccessWorkerClassificationPopup: false,
  showConnectionsHubPopup: false,
  showWorkerClassificationManagementPopup: false,
  stateWorkerClassification: null,
  workerClassificationManagement: {
    unselected: [],
    confirmed: [],
    pending: [],
  },
  gotuTerms: {
    accepted: true,
    date: null,
  },
  professionalProfile: null,
};

export default (
  state: IUserReducerState = initialState,
  action: TUserAction,
): IUserReducerState => {
  switch (action.type) {
    case 'SELECT_OFFICE_REQUEST': {
      return {
        ...initialState,
        childOffices: state.childOffices,
        user: state.user,
        selectedOfficeId: action.payload,
      };
    }
    case 'SEARCH_OFFICE_ACTION_SUCCESS': {
      const text = (action.payload as string).toLowerCase();
      const filteredChilds = state.childOffices.filter((office) =>
        office.office_name.toLowerCase().includes(text),
      );

      return {
        ...state,
        filteredChilds,
      };
    }
    case 'VERIFY_PARTNERSHIP_CODE_SUCCESS': {
      const { code, organization } = action.payload as { code: string; organization: string };
      return {
        ...state,
        selectedChildOffice: {
          ...state.selectedChildOffice,
          partnerReferral: {
            partnershipCode: {
              code,
              organization,
            },
          } as IPartnerReferral,
        } as TSelectedChildOffice,
        showPartnershipCodeSuccess: true,
      };
    }
    case HIDE_PARTNERSHIP_CODE_SUCCESS_POPUP:
      return {
        ...state,
        showPartnershipCodeSuccess: false,
      };
    case SHOW_REGISTRATION_SUCCESS_POPUP:
      return {
        ...state,
        showRegistrationSuccessPopup: true,
      };
    case HIDE_REGISTRATION_SUCCESS_POPUP:
      return {
        ...state,
        showRegistrationSuccessPopup: false,
      };
    case apiMethodsConst.UPDATE_USER_EDUCATION_REQUEST:
    case apiMethodsConst.EDIT_USER_REQUEST: {
      const updatedUser = action.payload;

      if (!updatedUser) {
        return state;
      }

      const newMaster =
        state.user?.id === updatedUser.id ? { user: updatedUser } : { user: state.user };
      const newSelectedChildOffice =
        state.selectedChildOffice?.id === updatedUser.id
          ? { selectedChildOffice: updatedUser }
          : { selectedChildOffice: state.selectedChildOffice };

      return {
        ...state,
        ...newMaster,
        ...newSelectedChildOffice,
      };
    }
    case apiMethodsConst.UPLOAD_AVATAR_REQUEST: {
      const { profile_image, officeId } = action.payload as {
        profile_image: string;
        officeId?: string;
      };
      // A Location settings upload names its office: patch only the records that are it.
      if (officeId) {
        return {
          ...state,
          ...(state.user?.id === officeId && {
            user: { ...state.user, profile_image } as IUser,
          }),
          ...(state.selectedChildOffice?.id === officeId && {
            selectedChildOffice: {
              ...state.selectedChildOffice,
              profile_image,
            } as TSelectedChildOffice,
          }),
        };
      }
      return {
        ...state,
        ...(state.selectedChildOffice
          ? {
              selectedChildOffice: {
                ...state.selectedChildOffice,
                profile_image,
              } as TSelectedChildOffice,
            }
          : {
              user: {
                ...state.user,
                profile_image,
              } as IUser,
            }),
      };
    }
    case apiMethodsConst.BLOCK_PROFESSIONAL_REQUEST: {
      const { blocked_hygienist } = action.payload as {
        blocked_hygienist: Array<{ id: string }>;
      };
      return {
        ...state,
        user: {
          ...state.user,
          blocked_hygienist: blocked_hygienist.map(({ id }: { id: string }) => id),
        } as IUser,
      };
    }
    case apiMethodsConst.AUTH_ME_REQUEST: {
      const { childOffices, ...rest } = action.payload || {};
      sessionStorage.setItem('UserDetail', JSON.stringify(rest));
      sessionStorage.setItem('loggedIn', JSON.stringify(true));

      const selectedOfficeId = localStorage.getItem('selectedOfficeId');

      const selectedOffice =
        childOffices?.find((office: IUser) => office.id === selectedOfficeId) || childOffices[0];

      return {
        ...state,
        user: rest,
        ...(!shouldUseDsoStructure()
          ? {
              childOffices: childOffices?.length > 1 ? childOffices : [],
              selectedOfficeId: childOffices?.length > 1 ? selectedOffice?.id : null,
              selectedChildOffice:
                childOffices && childOffices?.length >= 0 ? childOffices[0] : null,
            }
          : {}),
      };
    }
    case BestInDental.DISMISS_WINNER_POPUP: {
      const { year } = (action as TDismissWinnerPopupAction).payload;

      // Determine which office has the bestInDental data
      const targetOffice = state.selectedChildOffice || state.user;

      // Guard clause: ensure office and bestInDental data exist
      if (!targetOffice?.office_info?.bestInDental?.winnerDetails) {
        return state;
      }

      // Generate timestamp when dismissing
      const timestamp = new Date().toISOString();

      // Update the winnerDetails array immutably
      const updatedWinnerDetails = targetOffice.office_info.bestInDental.winnerDetails.map(
        (detail) =>
          detail.winningYear === year ? { ...detail, popupDismissedAt: timestamp } : detail,
      );

      // Helper function to update office with new winnerDetails
      const updateOfficeWithWinnerDetails = (office: IUser | TSelectedChildOffice) => ({
        ...office,
        office_info: {
          ...office?.office_info,
          bestInDental: {
            ...office?.office_info.bestInDental,
            winnerDetails: updatedWinnerDetails,
          },
        },
      });

      // Check if selectedChildOffice ID matches state.user ID
      const isSameOffice = state.selectedChildOffice?.id === state.user?.id;

      // Update selectedChildOffice if it exists
      const newSelectedChildOffice = state.selectedChildOffice
        ? {
            selectedChildOffice: updateOfficeWithWinnerDetails(
              state.selectedChildOffice,
            ) as TSelectedChildOffice,
          }
        : {};

      // Update user if: 1) no selectedChildOffice exists, OR 2) selectedChildOffice is same as user
      const newUser =
        !state.selectedChildOffice || isSameOffice
          ? { user: updateOfficeWithWinnerDetails(state.user) as IUser }
          : {};

      return {
        ...state,
        ...newUser,
        ...newSelectedChildOffice,
      };
    }
    case apiMethodsConst.GET_CHILD_OFFICES_REQUEST: {
      const childOffices = action.payload || [];

      return {
        ...state,
        offices: childOffices,
        childOffices: childOffices?.length > 1 ? childOffices : [],
        selectedOfficeId: childOffices?.length === 1 ? childOffices[0].id : null,
        selectedChildOffice:
          childOffices?.length === 1 ? (state.user as TSelectedChildOffice) : null,
      };
    }
    case 'SET_SELECTED_OFFICE':
    case apiMethodsConst.GET_SELECTED_OFFICE_PROFILE_REQUEST:
      return {
        ...state,
        selectedChildOffice:
          state.selectedChildOffice?.id === action.payload?.id ||
          state.selectedOfficeId === action.payload?.id
            ? action.payload
            : state.selectedChildOffice,
        user: state.user?.id === action.payload?.id ? action.payload : state.user,
      };
    case apiMethodsConst.AUTH_LOGOUT_REQUEST:
      localStorage.removeItem('refresh_token_dental');
      localStorage.removeItem('selectedOfficeId');

      return initialState;
    case apiMethodsConst.DELETE_ADDRESSES_REQUEST:
      return {
        ...state,
        user: {
          ...state.user,
          address: [],
        } as IUser,
      };
    case apiMethodsConst.ADD_ADDRESS_REQUEST:
      return {
        ...state,
        user: {
          ...state.user,
          address: [action.payload],
        } as IUser,
      };
    case apiMethodsConst.SEND_SMS_CODE_REQUEST:
      return {
        ...state,
        pendingPhoneVerification: true,
      };
    case apiMethodsConst.PHONE_VERIFICATION_REQUEST:
    case apiMethodsConst.CLOSE_MODAL_ACTION:
      return {
        ...state,
        pendingPhoneVerification: false,
      };
    case NPS_REVIEW_SUBMITTED: {
      return {
        ...state,
        user: {
          ...state.user,
          show_nps: false,
        } as IUser,
      };
    }
    case apiMethodsConst.GET_ALL_SPECIALIZATIONS_REQUEST: {
      return {
        ...state,
        allSpecializations: action.payload,
      };
    }
    case apiMethodsConst.GET_USER_EDUCATION_REQUEST: {
      return {
        ...state,
        userEducation: action.payload,
      };
    }
    case apiMethodsConst.GET_OFFICE_EDUCATION_REQUEST: {
      return {
        ...state,
        officeEducation: action.payload?.DNT,
      };
    }
    case apiMethodsConst.GET_ALL_SOFTWARES_REQUEST: {
      return {
        ...state,
        allSoftwares: action.payload,
      };
    }
    case apiMethodsConst.FETCH_NOTIFICATION_SETTINGS_REQUEST: {
      return {
        ...state,
        notificationSettings: action.payload,
      };
    }
    case apiMethodsConst.FETCH_FAVORITE_PROFESSIONALS_REQUEST:
      return {
        ...state,
        favoriteProfessionals: action.payload,
        isProfessionalHubInitialized: true,
      };
    case apiMethodsConst.FETCH_RECENT_PROFESSIONALS_REQUEST:
      return {
        ...state,
        recentProfessionals: action.payload,
        isProfessionalHubInitialized: true,
      };
    case apiMethodsConst.FETCH_RECOMMENDED_PROFESSIONALS_REQUEST: {
      const payload = action.payload as { data: IRecommendedProfessional[] } | undefined;
      return {
        ...state,
        recommendedProfessionals: payload?.data || [],
        isProfessionalHubInitialized: true,
      };
    }
    /* Fix saga and reducer. Action payload is incorrect. Add test */
    case 'ADD_FAVORITE_PROFESSIONAL_SUCCESS':
      return {
        ...state,
        user: {
          ...state.user,
          favoriteProfessionals: [...state.favoriteProfessionals, action.payload],
        } as IUser,
      };
    case apiMethodsConst.DELETE_FAVORITE_PROFESSIONAL_REQUEST:
      return {
        ...state,
        favoriteProfessionals: state.favoriteProfessionals.filter(
          ({ id }) => id !== action.payload,
        ),
      };
    case apiMethodsConst.FETCH_PARTNERSHIP_ORGANIZATIONS_REQUEST:
      return {
        ...state,
        partnershipOrganizations: action.payload,
      };
    case apiMethodsConst.FETCH_METADATA_REQUEST:
      return {
        ...state,
        allMetadata: action.payload,
      };
    case apiMethodsConst.SAVE_WORKER_CLASSIFICATION_REQUEST: {
      const isPendingWCRequest =
        action.payload?.workerClassificationRequest?.[0]?.status === 'requested';

      return {
        ...state,
        showSuccessWorkerClassificationPopup: isPendingWCRequest ? 'requested' : 'confirmed',
        user: {
          ...action.payload,
        },
      };
    }
    case apiMethodsConst.GET_WORKER_CLASSIFICATION_MANAGEMENT:
      return {
        ...state,
        workerClassificationManagement: action.payload,
      };
    case apiMethodsConst.SAVE_CHILD_WORKER_CLASSIFICATION:
      return {
        ...state,
        showWorkerClassificationManagementPopup: WC_MANAGEMENT_STATUS.CONFIRMED,
      };
    case apiMethodsConst.SAVE_STATUS_CHILD_WORKER_CLASSIFICATION: {
      const { status } = action.payload as { status: string };
      return {
        ...state,
        showWorkerClassificationManagementPopup: status,
      };
    }
    case apiMethodsConst.GET_STATE_WORKER_CLASSIFICATION_REQUEST:
      return {
        ...state,
        stateWorkerClassification: action.payload,
      };
    case 'HIDE_SUCCESS_WORKER_CLASSIFICATION_POPUP':
      return {
        ...state,
        showSuccessWorkerClassificationPopup: false,
        showWorkerClassificationManagementPopup: false,
      };
    case SHOW_CONNECTIONS_HUB_POPUP:
      return {
        ...state,
        showConnectionsHubPopup: true,
      };

    case HIDE_CONNECTIONS_HUB_POPUP:
      return {
        ...state,
        showConnectionsHubPopup: false,
      };
    case CHANGE_HUB_FILTER:
      return {
        ...state,
        professionalHubFilter: action.payload as string,
      };

    case apiMethodsConst.GET_TERMS_AND_CONDITIONS_FOR_STAFF_REQUEST: {
      const { data } = action.payload as {
        data: { accepted: boolean; date: string | null };
      };
      return {
        ...state,
        gotuTerms: {
          accepted: data.accepted,
          date: data.date,
        },
      };
    }
    case apiMethodsConst.FETCH_PROFESSIONAL_PROFILE_REQUEST: {
      return {
        ...state,
        professionalProfile: action.payload as IUser,
      };
    }
    default:
      return state;
  }
};
